import React, { useState, useMemo } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
} from "react-router-dom";
import { useSelector } from "react-redux";
import { ThemeProvider, createTheme, CssBaseline } from "@mui/material";
import { AppContext } from "./context";
import SecuredRoute from "@smfa/utils/src/components/SecuredRoute";
import AdminLayout from "./ui-components/AdminLayout";
import { getDesignTokens } from "@smfa/utils";

// Pages
//------------------------------------------------------------------------------
import Joinsmfa from "./pages/joinsmfa";
import Home from "./pages/home";
import About from "./pages/about";
import Mission from "./pages/mission";
import Getinvolve from "./pages/getinvolve";
import GetInvolvedMembership from "./pages/getinvolvedmembership";
import Contentmanagement from "./pages/contentmanagement";
import Directors from "./pages/directors";
import Meeting from "./components/meeting";
import Benefitsmembership from "./pages/benefitsmembership";
import Memberhighlight from "./components/memberhighlight";
import Membershipcategories from "./pages/membershipcategories";
//import Contact from "./pages/contact";
import Patients from "./components/patients";
import Educationmeetingplanningcommittee from "./components/educationmeetingplanningcommittee";
import Financemembershipcommittee from "./components/financemembershipcommittee";
import Researchcommittee from "./components/researchcommittee";
import Intersocietycommittee from "./components/intersocietycommittee";
import Committeemembers from "./components/committeemembers";
import Affiliates from "./components/affiliates";
import Site from "./pages/site";
import Comingsoon from "./pages/comingsoon";
import Members from "./pages/members";
import MeetingRegistrants from "./pages/meeting-registrants";
import FiestaSessions from "./pages/fiestasessions";
import Privacypolicy from "./components/privacypolicy";
import Cancelpolicy from "./components/cancelpolicy";
import Login from "./pages/login";
import Meetings from "./pages/meetings";

import "./App.css";

/**
 * Fallback
 * @returns {JSX.Element}
 * @component
 */
const NoMatch = () => <Navigate to="/" />;

/**
 *
 * @returns {JSX.Element}
 * @component
 */
const App = () => {
  var data = [];
  var baseName = "/";
  data.tabName = "home";
  data.backEndUrl = process.env.REACT_APP_BACKEND_URL + "/api";
  data.imageEndUrl = process.env.REACT_APP_IMAGE_URL;

  const [storage, setStorage] = useState([data]);
  // eslint-disable-next-line no-unused-vars
  const { isAuthenticated } = useSelector((state) => state.auth);
  const user = useSelector((state) => state.user);
  // Theme mode state with light mode as default - Present but not active
  const [mode, setMode] = useState("light");

  // Site routes - must be stable across re-renders; RouterProvider does not
  // support swapping the router object, so recreating it on every render
  // breaks in-flight navigations (e.g. login -> /home).
  const router = useMemo(
    () =>
      createBrowserRouter(
        [
          // Root route for login
          { path: "/", element: <Login /> },
          // Protected routes
          {
            //element: <RequireAuth userdata={userdata || null} />,
            element: (
              <SecuredRoute
                requiredRole="admin"
                successRoute="/home"
                loginRoute="/"
              >
                <Outlet />
              </SecuredRoute>
            ),
            children: [
              {
                element: <AdminLayout />,
                children: [
                  { path: "/home", element: <Home /> },
                  { path: "/joinsmfa", element: <Joinsmfa /> },
                  { path: "/mission", element: <Mission /> },
                  { path: "/getinvolve", element: <Getinvolve /> },
                  { path: "/about", element: <About /> },
                  {
                    path: "/contentmanagement",
                    element: <Contentmanagement />,
                  },
                  { path: "/directors", element: <Directors /> },
                  { path: "/meeting", element: <Meeting /> },
                  {
                    path: "/benefitsmembership",
                    element: <Benefitsmembership />,
                  },
                  {
                    path: "/membershipbanner",
                    element: <Navigate to="/benefitsmembership" replace />,
                  },
                  { path: "/memberhighlight", element: <Memberhighlight /> },
                  {
                    path: "/membershipcategories",
                    element: <Membershipcategories />,
                  },
                  {
                    path: "/getinvolvedmembership",
                    element: <GetInvolvedMembership />,
                  },
                  //{ path: "/contact", element: <Contact /> },
                  { path: "/patients", element: <Patients /> },
                  {
                    path: "/educationmeetingplanningcommittee",
                    element: <Educationmeetingplanningcommittee />,
                  },
                  {
                    path: "/financemembershipcommittee",
                    element: <Financemembershipcommittee />,
                  },
                  {
                    path: "/researchcommittee",
                    element: <Researchcommittee />,
                  },
                  {
                    path: "/intersocietycommittee",
                    element: <Intersocietycommittee />,
                  },
                  { path: "/committeemembers", element: <Committeemembers /> },
                  { path: "/affiliates", element: <Affiliates /> },
                  { path: "/site", element: <Site /> },
                  { path: "/comingsoon", element: <Comingsoon /> },
                  { path: "/members", element: <Members /> },
                  { path: "/meetings", element: <Meetings /> },
                  {
                    path: "/meeting-registrants",
                    element: <MeetingRegistrants />,
                  },
                  { path: "/fiestasessions", element: <FiestaSessions /> },
                  { path: "/privacypolicy", element: <Privacypolicy /> },
                  { path: "/cancelpolicy", element: <Cancelpolicy /> },
                ],
              },
            ],
          },
          { path: "*", element: <NoMatch /> },
        ],
        { basename: baseName },
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const dispatchStorageEvent = (payload) => {
    setStorage(payload);
    return true;
  };

  // Create theme with the appropriate mode
  const theme = useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  // Theme toggling function to be passed to components that need it
  const toggleColorMode = () => {
    setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
  };

  log.debug("Admin App - Auth state:", { isAuthenticated, user });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppContext.Provider
        value={{
          storage,
          dispatchStorageEvent,
          mode,
          toggleColorMode, // Make theme toggle available through context
        }}
      >
        <RouterProvider router={router} />
      </AppContext.Provider>
    </ThemeProvider>
  );
};

export default App;
